import { useEffect, useState } from "react";
import { fetchSportNews } from "@/lib/strapi";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 12,
  pageCount: 1,
  total: 0,
};

const getUniqueSportNewsArticles = (currentArticles, incomingArticles) => {
  const seenArticleIds = new Set(currentArticles.map((article) => article.slug || article.id));
  return incomingArticles.filter((article) => {
    const key = article.slug || article.id;
    if (!key || seenArticleIds.has(key)) {
      return false;
    }

    seenArticleIds.add(key);
    return true;
  });
};

const mergeSportNewsArticles = (currentArticles, incomingArticles) => {
  return [...currentArticles, ...getUniqueSportNewsArticles(currentArticles, incomingArticles)];
};

export function useSportNews(limit = 12) {
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: limit });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadArticles = async () => {
      try {
        setIsLoading(true);
        setError("");
        const result = await fetchSportNews({ page: 1, pageSize: limit });
        if (mounted) {
          const nextArticles = result?.items || [];
          const nextPagination = result?.pagination || { ...DEFAULT_PAGINATION, pageSize: limit };

          setArticles(nextArticles);
          setPagination(nextPagination);
          setHasMore(nextArticles.length > 0);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to load sport news");
          setArticles([]);
          setPagination({ ...DEFAULT_PAGINATION, pageSize: limit });
          setHasMore(false);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadArticles();

    return () => {
      mounted = false;
    };
  }, [limit]);

  const loadMore = async () => {
    if (isLoading || isLoadingMore || !hasMore) {
      return;
    }

    try {
      setIsLoadingMore(true);
      setError("");
      const nextPage = pagination.page + 1;
      const result = await fetchSportNews({ page: nextPage, pageSize: limit });
      const incomingArticles = result?.items || [];
      const uniqueIncomingArticles = getUniqueSportNewsArticles(articles, incomingArticles);
      const resultPagination = result?.pagination || pagination;
      const nextPagination = {
        ...resultPagination,
        page: Math.max(Number(resultPagination.page) || nextPage, nextPage),
      };

      setArticles((currentArticles) => mergeSportNewsArticles(currentArticles, incomingArticles));
      setPagination(nextPagination);
      setHasMore(
        uniqueIncomingArticles.length > 0 &&
          (nextPagination.page < nextPagination.pageCount || incomingArticles.length >= limit)
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load sport news");
    } finally {
      setIsLoadingMore(false);
    }
  };

  return {
    articles,
    pagination,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  };
}
